/**
 * Shift-type colour palette shared by every shift pill/card surface: the v2
 * Build workspace, the wizard's `PreviewModal`, and the proposal's Staff Group
 * view.
 *
 * Eight cohesive hues, each backed by theme tokens defined in `app.css`
 * (`--shift-<name>*`), so pills respect light/dark mode and host theming rather
 * than hardcoding colours. Exposed here as literal Tailwind utility strings so
 * Tailwind's scanner picks them up.
 *
 * `preview-utils.ts` used to have its own separate, unthemed `getShiftColor`
 * (a generic Tailwind rainbow, disconnected from both this palette and WFM's
 * real per-shift-type colours) — that's been cut over to this one so every
 * preview surface renders shift types consistently.
 */

/** Name of a palette colour. */
export type ShiftColourName =
  | 'teal'
  | 'indigo'
  | 'sky'
  | 'coral'
  | 'emerald'
  | 'violet'
  | 'amber'
  | 'rose';

/** All palette colour names, in declaration order. */
export const SHIFT_COLOUR_NAMES: ShiftColourName[] = [
  'teal',
  'indigo',
  'sky',
  'coral',
  'emerald',
  'violet',
  'amber',
  'rose',
];

/** Tailwind class bundles for the visual roles a shift colour fills. */
export interface ShiftPillClasses {
  /** Solid pill: filled background, matching border, white text. */
  solid: string;
  /** Draft/ghost pill: subtle background, dashed subtle border, subtle text. */
  draft: string;
  /** Small swatch (e.g. the pattern-card dot): filled background + border. */
  swatch: string;
}

/**
 * Literal Tailwind classes per colour, backed by the `--color-shift-*` theme
 * tokens. Kept as full literal strings (not interpolated) so Tailwind's scanner
 * includes them in the build.
 */
export const SHIFT_PILL_CLASSES: Record<ShiftColourName, ShiftPillClasses> = {
  teal: {
    solid: 'bg-shift-teal border-shift-teal-border text-white',
    draft: 'bg-shift-teal-subtle border-shift-teal-subtle-border text-shift-teal-subtle-fg',
    swatch: 'bg-shift-teal border-shift-teal-border',
  },
  indigo: {
    solid: 'bg-shift-indigo border-shift-indigo-border text-white',
    draft: 'bg-shift-indigo-subtle border-shift-indigo-subtle-border text-shift-indigo-subtle-fg',
    swatch: 'bg-shift-indigo border-shift-indigo-border',
  },
  sky: {
    solid: 'bg-shift-sky border-shift-sky-border text-white',
    draft: 'bg-shift-sky-subtle border-shift-sky-subtle-border text-shift-sky-subtle-fg',
    swatch: 'bg-shift-sky border-shift-sky-border',
  },
  coral: {
    solid: 'bg-shift-coral border-shift-coral-border text-white',
    draft: 'bg-shift-coral-subtle border-shift-coral-subtle-border text-shift-coral-subtle-fg',
    swatch: 'bg-shift-coral border-shift-coral-border',
  },
  emerald: {
    solid: 'bg-shift-emerald border-shift-emerald-border text-white',
    draft:
      'bg-shift-emerald-subtle border-shift-emerald-subtle-border text-shift-emerald-subtle-fg',
    swatch: 'bg-shift-emerald border-shift-emerald-border',
  },
  violet: {
    solid: 'bg-shift-violet border-shift-violet-border text-white',
    draft: 'bg-shift-violet-subtle border-shift-violet-subtle-border text-shift-violet-subtle-fg',
    swatch: 'bg-shift-violet border-shift-violet-border',
  },
  amber: {
    solid: 'bg-shift-amber border-shift-amber-border text-white',
    draft: 'bg-shift-amber-subtle border-shift-amber-subtle-border text-shift-amber-subtle-fg',
    swatch: 'bg-shift-amber border-shift-amber-border',
  },
  rose: {
    solid: 'bg-shift-rose border-shift-rose-border text-white',
    draft: 'bg-shift-rose-subtle border-shift-rose-subtle-border text-shift-rose-subtle-fg',
    swatch: 'bg-shift-rose border-shift-rose-border',
  },
};

/**
 * Best-effort mapping of well-known English shift-type names to a palette
 * colour. Shift-type names are tenant free-text/locale strings, so unmatched
 * names fall back to a deterministic hash (see {@link getShiftColour}).
 */
const KNOWN_SHIFT_TYPES: Record<string, ShiftColourName> = {
  'Bar work': 'teal',
  Bartender: 'violet',
  Server: 'sky',
  'Line cook': 'coral',
  'Kitchen porter': 'emerald',
  Chef: 'indigo',
  Manager: 'amber',
};

/**
 * Resolves the palette colour name for a shift type: the designated colour for
 * a known name, otherwise a deterministic hash so the same name always gets the
 * same colour. Empty/falsy types fall back to teal (the brand colour).
 */
export function getShiftColour(shiftType: string): ShiftColourName {
  if (!shiftType) return 'teal';

  const known = KNOWN_SHIFT_TYPES[shiftType];
  if (known) return known;

  let hash = 0;
  for (let i = 0; i < shiftType.length; i++) {
    hash = (hash * 31 + shiftType.charCodeAt(i)) | 0;
  }
  return SHIFT_COLOUR_NAMES[Math.abs(hash) % SHIFT_COLOUR_NAMES.length];
}
